import { ArrayUtil, DbDisplayValue, ObjectUtil, StringUtil } from "@mcleod/core";
import {
    AllowedUse, AllowedUseAddlOption, AllowedUseAddlOptionRegistry, ChangeEvent, Checkbox, CheckboxProps, DecoratorType,
    DialogProps, Label, Panel
} from "@mcleod/components";
import { MultipleValueSelector } from "./MultipleValueSelector";

export class LayoutUseSelector extends MultipleValueSelector {
    private _allowedUseGroups: Map<string, AllowedUseGroup>;

    override getDialogProps(): Partial<DialogProps> {
        return {
            ...super.getDialogProps(),
            height: 750,
            okDefault: false,
            focusContentOnShow: false
        }
    }

    protected override getTitle(): string {
        return "Select Layout Uses";
    }

    protected getHeadingLabelText(): string {
        return "Specify how the layout can be used.";
    }

    protected discoverValidValues(): DbDisplayValue[] {
        const result: DbDisplayValue[] = [];
        for (const allowedUseGroup of this.allowedUseGroups.values()) {
            result.push(allowedUseGroup.dbDisplayValue);
        }
        return result;
    }

    protected markSelectedValues(allowedUses: any) {
        const uses: AllowedUse[] = allowedUses.uses;
        const defaultUse: AllowedUse = allowedUses.defaultUse;
        const addlOptions: string[] = allowedUses.addlOptions;
        for (const allowedUseGroup of this.allowedUseGroups.values()) {
            allowedUseGroup.setStateFromValues(uses, defaultUse, addlOptions);
        }
    }

    private get allowedUseGroups(): Map<string, AllowedUseGroup> {
        if (this._allowedUseGroups == null) {
            const groups = [ this.crudGroup, this.reportGroup, this.taskGroup, this.listGroup ];
            this._allowedUseGroups = new Map(groups.map(group => [group.id, group]));
        }
        return this._allowedUseGroups;
    }

    private get crudGroup(): AllowedUseGroup {
        const groupDbValue = "crud";
        const groupDisplayValue = "Add/Update/Delete/Search Operations";
        const uses= [ AllowedUse.ADD, AllowedUse.UPDATE, AllowedUse.DELETE, AllowedUse.SEARCH ];
        const defaultUses = [ AllowedUse.ADD, AllowedUse.UPDATE ];
        const description = "Layouts that are used for add, update, delete, or search operations typically display " +
            "data for a single record (and any of the record's child data).  It is not uncommon for a screen to be " +
            "used for all four actions.";
        return new AllowedUseGroup(groupDbValue, groupDisplayValue, uses, description, defaultUses);
    }

    private get reportGroup(): AllowedUseGroup {
        const groupDbValue = AllowedUse.REPORT;
        const groupDisplayValue = "Report Generation";
        const uses = [ AllowedUse.REPORT ];
        const description = "Layouts that are used to generate reports contain input fields that are used to specify " +
            "information to allow for the report(s) to be run.";
        return new AllowedUseGroup(groupDbValue, groupDisplayValue, uses, description);
    }

    private get taskGroup(): AllowedUseGroup {
        const groupDbValue = AllowedUse.TASK;
        const groupDisplayValue = "Task Processing";
        const uses = [ AllowedUse.TASK ];
        const description = "Layouts that are used to complete tasks contain input fields that are used to specify " +
            "information to allow for the task to be executed.  Task layouts typically offer a preview action (such " +
            "as an edit list) an execute action (which actually performs the task).  The task typically returns " +
            "reports or some other form of output that displays the result of the task.";
        return new AllowedUseGroup(groupDbValue, groupDisplayValue, uses, description);
    }

    private get listGroup(): AllowedUseGroup {
        const groupDbValue = "list";
        const groupDisplayValue = "List Display";
        const uses: AllowedUse[] = ["list"];
        const description = "Layouts that display a single table of data, allowing users to add, update, or delete " +
            "records based on the table's configuration. The first table in this layout will be configured to persist changes immediately.";
        return new AllowedUseGroup(groupDbValue, groupDisplayValue, uses, description);
    }

    protected addCheckboxes() {
        const panelCheckboxes = super.createPanelCheckboxes();
        this.checkboxes.forEach(checkbox => {
            checkbox.addChangeListener((event: ChangeEvent) => this.onGroupCheckboxChange(event));
            const allowedUseGroup = this.allowedUseGroups.get(checkbox.valueChecked);
            allowedUseGroup.groupCheckbox = checkbox;
            const groupPanel = allowedUseGroup.buildPanel();
            panelCheckboxes.add(groupPanel);
        });
        this.add(panelCheckboxes);
    }

    private onGroupCheckboxChange(event: ChangeEvent) {
        if (!event.userInitiatedChange) {
            return;
        }
        const updatedCheckbox = event.target as Checkbox;
        const selectedAllowedUseGroup =
            updatedCheckbox.checked ? this.allowedUseGroups.get(updatedCheckbox.valueChecked) : null;
        for (const allowedUseGroup of this.allowedUseGroups.values()) {
            const groupIsSelected = allowedUseGroup === selectedAllowedUseGroup;
            allowedUseGroup.updateCheckboxes(groupIsSelected);
        }
    }

    public override getResultValue(): any {
        const resultJson = this.getResultJson();
        return resultJson != null ? JSON.stringify(resultJson) : null;
    }

    private getResultJson(): any {
        const result: any = {
            uses: []
        };
        this.allowedUseGroups.forEach(allowedUseGroup => {
            result.uses.push(...allowedUseGroup.selectedAllowedUses);
            const defaultUse = allowedUseGroup.selectedDefaultUse;
            if (defaultUse != null) {
                result.defaultUse = defaultUse;
            }
            const addlOptions = allowedUseGroup.selectedAddlOptions.map(option => option.key);
            if (!ArrayUtil.isEmptyArray(addlOptions)) {
                result.addlOptions = addlOptions;
            }
        });
        return !ArrayUtil.isEmptyArray(result.uses) ? result : null;
    }
}

class AllowedUseGroup {
    dbDisplayValue: DbDisplayValue;
    private descr: string;
    groupCheckbox: Checkbox;

    // Defines the possible uses to present in the group, and the checkboxes for those uses.
    private uses: AllowedUse[];
    private checkboxesByUse: Map<AllowedUse, Checkbox> = new Map();

    // Defines the possible default uses to present in the group, and the checkboxes for those uses.
    // Delete is not a valid default use, but it makes the code cleaner to use AllowedUse in this collection.
    // If delete is provided as a default use it will be ignored.
    private defaultUses: AllowedUse[];
    private defaultCheckboxesByUse: Map<AllowedUse, Checkbox> = new Map();

    // Defines extra options that are available for the AllowedUseGroup
    private addlOptions: AllowedUseAddlOption[];
    private addlOptionsCheckboxes: Map<AllowedUseAddlOption, Checkbox> = new Map();

    constructor(dbValue: string, displayValue: string, uses: AllowedUse[], descr: string,
        defaultUses: AllowedUse[] = uses) {
        this.dbDisplayValue = new DbDisplayValue(dbValue, displayValue);
        this.uses = uses ?? [];
        this.descr = descr ?? "";
        // Ignore default values that are not DecoratorType values.
        this.defaultUses = (defaultUses ?? []).filter(defaultUse => ObjectUtil.isEnumValue(DecoratorType, defaultUse));
        this.addlOptions =
            AllowedUseAddlOptionRegistry.get().getAll().filter(option => this.uses.includes(option.parent)) ?? [];
    }

    get selectedAllowedUses(): AllowedUse[] {
        const result: AllowedUse[] = [];
        this.checkboxesByUse.forEach((checkbox: Checkbox, use: AllowedUse) => {
            if (checkbox.checked === true) {
                result.push(use);
            }
        });
        return result;
    }

    get selectedDefaultUse(): AllowedUse {
        let result: AllowedUse = null;
        this.defaultCheckboxesByUse.forEach((defaultCheckbox: Checkbox, defaultUse: AllowedUse) => {
            if (defaultCheckbox.checked === true && this.defaultUses.includes(defaultUse)) {
                result = defaultUse;
            }
        });
        return result;
    }

    get selectedAddlOptions(): AllowedUseAddlOption[] {
        const result: AllowedUseAddlOption[] = [];
        this.addlOptionsCheckboxes.forEach((addlOptionCheckbox: Checkbox, addlOption: AllowedUseAddlOption) => {
            if (addlOptionCheckbox.checked === true) {
                result.push(addlOption);
            }
        });
        return result;
    }

    get id(): string {
        return this.dbDisplayValue.value;
    }

    private getInnerComponentId(value: string): string {
        return `group-${this.id}-${value}`;
    }

    buildPanel(): Panel {
        const groupPanel = Panel.createNoMarginNoPaddingPanel({
            id: this.getInnerComponentId("panel"),
            fillRow: true,
            marginTop: 15,
            rowBreak: true,
            scrollY: false
        });
        const panelUses = Panel.createNoMarginNoPaddingPanel({
            id: this.getInnerComponentId("panelUses"),
            marginLeft: 25,
            minWidth: 150,
            rowBreak: false,
            scrollY: false
        });
        const panelDefaultUses = Panel.createNoMarginNoPaddingPanel({
            id: this.getInnerComponentId("panelDefaultUses"),
            marginLeft: 10,
            scrollY: false
        });
        this.uses.forEach((use: AllowedUse) => {
            const checkboxForUse = this.createCheckboxForUse(use);
            panelUses.add(checkboxForUse);
            const defaultCheckboxForUse = this.createDefaultCheckboxForUse(use);
            panelDefaultUses.add(defaultCheckboxForUse);
        });
        const panelAddlOptions = Panel.createNoMarginNoPaddingPanel({
            id: this.getInnerComponentId("panelAddlOptions"),
            marginLeft: 25,
            minWidth: 150,
            rowBreak: false,
            scrollY: false
        });
        this.addlOptions.forEach((option: AllowedUseAddlOption) => {
            const checkboxForAddlOption = this.createCheckboxForAddlOption(option);
            panelAddlOptions.add(checkboxForAddlOption);
        });
        this.groupCheckbox.id = this.getInnerComponentId("checkbox"),
        this.groupCheckbox.rowBreak = true;
        const labelDescr = this.createLabel(Number(panelUses.marginLeft) + 7);
        groupPanel.add(this.groupCheckbox, labelDescr);
        if (this.numberUsesInGroup > 1) {
            groupPanel.add(panelUses, panelDefaultUses);
        }
        if (this.numberAddlOptionsInGroup > 0) {
            groupPanel.add(panelAddlOptions);
        }
        return groupPanel;
    }

    private createLabel(marginLeft: number): Label {
        return new Label({
            id: this.getInnerComponentId("label"),
            marginLeft,
            text: this.descr
        });
    }

    private createCheckboxForUse(use: AllowedUse): Checkbox {
        const checkbox = new Checkbox({
            caption: `${StringUtil.capitalize(use)}`,
            enabled: false,
            id: this.getInnerComponentId(`checkbox-${use}`),
            valueChecked: use,
            valueUnchecked: null
        });
        checkbox.allowIndeterminate = false;
        checkbox.addChangeListener((event: ChangeEvent) => this.onUseCheckboxChange(event));
        this.checkboxesByUse.set(use, checkbox);
        return checkbox;
    }

    private onUseCheckboxChange(event: ChangeEvent) {
        if (!event.userInitiatedChange) {
            return;
        }
        const updatedCheckbox = event.target as Checkbox;
        const defaultCheckbox = this.defaultCheckboxesByUse.get(updatedCheckbox.valueChecked);
        if (updatedCheckbox.checked) {
            defaultCheckbox.enabled = true;
        } else {
            defaultCheckbox.checked = false;
            defaultCheckbox.enabled = false;
        }
    }

    private createDefaultCheckboxForUse(use: AllowedUse): Checkbox {
        const defaultCheckbox = new Checkbox({
            caption: "Use as Default",
            enabled: false,
            id: this.getInnerComponentId(`checkbox-${use}-default`),
            valueChecked: use,
            valueUnchecked: null,
            preventCollapse: true
        });
        defaultCheckbox.allowIndeterminate = false;
        defaultCheckbox.addChangeListener((event: ChangeEvent) => this.onDefaultCheckboxChange(event));
        this.defaultCheckboxesByUse.set(use, defaultCheckbox);
        // Hide default checkboxes that aren't valid, but preventCollapse so that spacing stays correct
        defaultCheckbox.visible = this.defaultUses.includes(use)
        return defaultCheckbox;
    }

    private onDefaultCheckboxChange(event: ChangeEvent) {
        if (!event.userInitiatedChange) {
            return;
        }
        const updatedCheckbox = event.target as Checkbox;
        if (!updatedCheckbox.checked) {
            return;
        }
        this.updateDefaultUseCheckboxes((checkbox: Checkbox) => { return { checked: updatedCheckbox === checkbox }});
    }

    private createCheckboxForAddlOption(option: AllowedUseAddlOption): Checkbox {
        const checkbox = new Checkbox({
            caption: option.descr,
            enabled: true,
            id: this.getInnerComponentId(`checkbox-${option.parent}-${option.key}`),
            valueChecked: option.key,
            valueUnchecked: null
        });
        checkbox.allowIndeterminate = false;
        this.addlOptionsCheckboxes.set(option, checkbox);
        return checkbox;
    }

    private get numberUsesInGroup(): number {
        return ArrayUtil.getLength(this.uses);
    }

    private get numberAddlOptionsInGroup(): number {
        return ArrayUtil.getLength(this.addlOptions);
    }

    updateCheckboxes(groupIsSelected: boolean) {
        const useCheckboxProps: Partial<CheckboxProps> = {};
        const defaultUseCheckboxProps: Partial<CheckboxProps> = {};
        const addlOptionCheckboxProps: Partial<CheckboxProps> = {};
        if (groupIsSelected) {
            const oneUseInGroup = this.numberUsesInGroup === 1;
            useCheckboxProps.checked = oneUseInGroup;
            useCheckboxProps.enabled = !oneUseInGroup;
            defaultUseCheckboxProps.checked = oneUseInGroup;
            defaultUseCheckboxProps.enabled = false;
            addlOptionCheckboxProps.enabled = true;
        } else {
            this.groupCheckbox.checked = false;
            useCheckboxProps.checked = false;
            useCheckboxProps.enabled = false;
            defaultUseCheckboxProps.checked = false;
            defaultUseCheckboxProps.enabled = false;
            addlOptionCheckboxProps.checked = false;
            addlOptionCheckboxProps.enabled = false;
        }
        this.updateUseCheckboxes(useCheckboxProps);
        this.updateDefaultUseCheckboxes(defaultUseCheckboxProps);
        this.updateAddlOptionCheckboxes(addlOptionCheckboxProps);
    }

    private updateUseCheckboxes(props: Partial<CheckboxProps>) {
        this.updateCheckboxesInMap(this.checkboxesByUse, props);
    }

    private updateDefaultUseCheckboxes(props: Partial<CheckboxProps>|((checkbox: Checkbox) => Partial<CheckboxProps>)) {
        this.updateCheckboxesInMap(this.defaultCheckboxesByUse, props);
    }

    private updateAddlOptionCheckboxes(props: Partial<CheckboxProps>|((checkbox: Checkbox) => Partial<CheckboxProps>)) {
        this.updateCheckboxesInMap(this.addlOptionsCheckboxes, props);
    }

    private updateCheckboxesInMap(checkboxMap: Map<AllowedUse|AllowedUseAddlOption, Checkbox>,
        props: Partial<CheckboxProps>|((checkbox: Checkbox) => Partial<CheckboxProps>)) {
        for (const checkbox of checkboxMap.values()) {
            const propsToUse = (typeof props === "function") ? props(checkbox) : props;
            checkbox.setProps(propsToUse);
        }
    }

    setStateFromValues(uses: AllowedUse[], defaultUse: AllowedUse, addlOptionKeys: string[]) {
        let activatingGroup = false;
        this.checkboxesByUse.forEach((checkbox: Checkbox, use: AllowedUse) => {
            if (uses?.includes(use)) {
                activatingGroup = true;
                checkbox.checked = true;
                this.enableDefaultCheckbox(use);
            }
        });
        const defaultCheckbox = this.defaultCheckboxesByUse.get(defaultUse);
        if (defaultCheckbox != null) {
            activatingGroup = true;
            if (this.defaultUses.includes(defaultUse)) {
                defaultCheckbox.checked = true;
            }
        }
        if (activatingGroup) {
            this.groupCheckbox.checked = true;
            this.groupCheckbox.addMountListener(() => this.groupCheckbox.scrollIntoView());
            this.updateUseCheckboxes({ enabled: true });
            this.checkboxesByUse.forEach((checkbox: Checkbox, use: AllowedUse) => {
                if (checkbox.checked) {
                    this.enableDefaultCheckbox(use);
                }
            });
            if (this.numberUsesInGroup === 1) {
                this.updateUseCheckboxes({ enabled: false });
                this.updateDefaultUseCheckboxes({ enabled: false });
            }

            addlOptionKeys?.forEach(addlOptionKey => {
                this.addlOptionsCheckboxes.forEach((checkbox: Checkbox, addlOption: AllowedUseAddlOption) => {
                    if (addlOption.key === addlOptionKey) {
                        checkbox.checked = true;
                    }
                });
            });
        }
    }

    private enableDefaultCheckbox(use: AllowedUse) {
        const defaultCheckbox = this.defaultCheckboxesByUse.get(use);
        if (defaultCheckbox != null) {
            defaultCheckbox.enabled = true;
        }
    }
}
